import {
  fetchAlarms,
  fetchAlarmCount,
  fetchBatteryAssetSummary,
  fetchGeneratorAssetSummary,
  fetchGridAssetSummary,
  fetchPvAssetSummary,
  fetchWeather,
} from './api.ts';

export interface DashboardWarmSnapshot {
  pv: unknown;
  bess: unknown;
  dg: unknown;
  grid: unknown;
  weather: unknown;
  alarms: { count?: number } | unknown;
  refreshedAt: number;
}

type DashboardWarmState = {
  siteId: string | null;
  snapshot: DashboardWarmSnapshot | null;
  inFlight: boolean;
  timerId: number | null;
};

const dashboardWarmState: DashboardWarmState = {
  siteId: null,
  snapshot: null,
  inFlight: false,
  timerId: null,
};

async function fetchDashboardSnapshot(siteId: string): Promise<DashboardWarmSnapshot> {
  const [pv, bess, dg, grid, weather, alarmsListResult, alarmsCountFallback] = await Promise.all([
    fetchPvAssetSummary(siteId) as Promise<unknown>,
    fetchBatteryAssetSummary(siteId) as Promise<unknown>,
    fetchGeneratorAssetSummary(siteId) as Promise<unknown>,
    fetchGridAssetSummary(siteId) as Promise<unknown>,
    fetchWeather(siteId) as Promise<unknown>,
    fetchAlarms({ siteId, state: 'active', limit: 1000 } as any).catch(() => [] as any[]),
    fetchAlarmCount(siteId, 'active').catch(() => ({ count: 0 } as { count: number })),
  ]);

  const activeCount = Array.isArray(alarmsListResult)
    ? alarmsListResult.length
    : Number((alarmsCountFallback as any)?.count || 0);
  const alarms = { count: activeCount };

  return {
    pv,
    bess,
    dg,
    grid,
    weather,
    alarms,
    refreshedAt: Date.now(),
  };
}

async function refreshDashboardSnapshot(siteId: string): Promise<DashboardWarmSnapshot> {
  if (dashboardWarmState.inFlight) {
    return dashboardWarmState.snapshot || (await fetchDashboardSnapshot(siteId));
  }

  dashboardWarmState.inFlight = true;
  try {
    const snapshot = await fetchDashboardSnapshot(siteId);
    dashboardWarmState.siteId = siteId;
    dashboardWarmState.snapshot = snapshot;
    return snapshot;
  } finally {
    dashboardWarmState.inFlight = false;
  }
}

export function getDashboardWarmSnapshot(siteId: string): DashboardWarmSnapshot | null {
  if (dashboardWarmState.siteId !== siteId) return null;
  return dashboardWarmState.snapshot;
}

export async function primeDashboardWarmSnapshot(siteId: string): Promise<DashboardWarmSnapshot> {
  return refreshDashboardSnapshot(siteId);
}

export function startDashboardWarmPolling(siteId: string, intervalMs = 10_000): void {
  if (dashboardWarmState.siteId !== siteId) {
    if (dashboardWarmState.timerId) {
      window.clearInterval(dashboardWarmState.timerId);
      dashboardWarmState.timerId = null;
    }
    dashboardWarmState.snapshot = null;
    dashboardWarmState.siteId = siteId;
  }

  if (dashboardWarmState.timerId) return;

  const tick = () => {
    void refreshDashboardSnapshot(siteId).catch((err) => {
      console.warn('[dashboardWarmCache] refresh failed:', err);
    });
  };

  tick();
  dashboardWarmState.timerId = window.setInterval(tick, intervalMs);
}
